import * as THREE from "three/webgpu";
import { objectAndAncestorsVisible } from "./objectVisibility.ts";

export interface SketchRaycastTarget {
  mesh: THREE.Mesh;
  objectId: string;
}

export function resolveSketchObjectId(obj: THREE.Object3D): string | null {
  let node: THREE.Object3D | null = obj;
  while (node) {
    const objectId = node.userData?.poserefObjectId;
    if (typeof objectId === "string" && objectId.length > 0) return objectId;
    node = node.parent;
  }
  return null;
}

function hasSketchTargetExcludeTag(obj: THREE.Object3D): boolean {
  let node: THREE.Object3D | null = obj;
  while (node) {
    if (node.userData?.excludeFromExport) return true;
    node = node.parent;
  }
  return false;
}

export function isSketchRaycastMesh(obj: THREE.Object3D): obj is THREE.Mesh {
  const mesh = obj as THREE.Mesh & {
    isLineSegments2?: boolean;
    _wireOverlay?: boolean;
    _depthPrepass?: boolean;
    _selectionOutlinePrepass?: boolean;
  };
  if (!mesh.isMesh) return false;
  if (mesh.isLineSegments2) return false;
  if (hasSketchTargetExcludeTag(mesh)) return false;
  if (
    mesh._wireOverlay ||
    mesh._depthPrepass ||
    mesh._selectionOutlinePrepass ||
    mesh.userData?.sketchOverlay
  ) {
    return false;
  }
  return true;
}

export function sketchTargetScore(mesh: THREE.Mesh): number {
  let score = 0;
  if ((mesh as THREE.SkinnedMesh).isSkinnedMesh) score += 100;
  if (mesh.name === "Mannequin_Body") score += 20;
  if (mesh.name.includes("Body")) score += 5;
  return score;
}

export function collectSketchRaycastTargets(
  scene: THREE.Scene,
  acceptsObjectId: (objectId: string) => boolean,
): SketchRaycastTarget[] {
  const targets: SketchRaycastTarget[] = [];
  scene.traverse((obj) => {
    if (!isSketchRaycastMesh(obj)) return;
    if (!objectAndAncestorsVisible(obj)) return;
    const objectId = resolveSketchObjectId(obj);
    if (!objectId || !acceptsObjectId(objectId)) return;
    targets.push({ mesh: obj as THREE.Mesh, objectId });
  });
  return targets;
}

export function buildSketchMeshIndex(
  scene: THREE.Scene,
  acceptsObjectId: (objectId: string) => boolean,
): Map<string, THREE.Mesh> {
  const meshByObject = new Map<string, THREE.Mesh>();
  for (const { mesh, objectId } of collectSketchRaycastTargets(
    scene,
    acceptsObjectId,
  )) {
    const existing = meshByObject.get(objectId);
    if (!existing || sketchTargetScore(mesh) > sketchTargetScore(existing)) {
      meshByObject.set(objectId, mesh);
    }
  }
  return meshByObject;
}
